/*****************************************************************************
 *   © Copyright 2021   Quantitative Software Management, Inc.               *
 *              --  All rights reserved.  --                                 *
 *   Unpublished -- rights reserved under the Copyright Laws                 *
 *   of the United States.                                                   *
 *                                                                           *
 *   This computer software is protected by trade secret, copyright law,     *
 *   and international treaties and contains proprietary and confidential    *
 *   trade secrets of Quantitative Software Management, Inc.  Unauthorized   *
 *   use, reproduction, or distribution of this program or any portion of    *
 *   it will result in severe civil or criminal penalties and will be        *
 *   prosecuted to the full extent of the law.                               *
 *                                                                           *
 *   Use, duplication, or disclosure by the Government is subject to         *
 *   restrictions as set forth in                                            *
 *      (i) a license agreement incorporated in the contract,                *
 *      (ii) the definition of "restricted rights" in paragraph (c)(1)(ii)   *
 *           of DFARS 252.227-7013 (1988), and                               *
 *      (iii) the definition of "restricted rights" in paragraph (a) of      *
 *            FAR 52.227-14, Alt III (Jun 1987) or                           *
 *      (iv) FAR 52.227-19 (Jun 1987), as applicable.                        *
 *   For purposes of the FAR, this software shall be deemed to               *
 *   be "unpublished" and licensed with disclosure prohibitions.             *
 *   Contractor/Manufacturer is   Quantitative Software Management, Inc.     *
 *                                                                           *
 *****************************************************************************/

import { forkJoin, Subscription } from 'rxjs';
import { Component, OnInit, ViewEncapsulation, ViewChild, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { GridComponent, RowClassArgs, SelectableSettings } from '@progress/kendo-angular-grid';
import { ChartService } from '../../../services/Chart.Service';
import { DashboardFolder, DashboardInfo, } from '../../../Models/chart-data';
import { DashboardTypeLabel } from '../../../app.enums';
import { LookupService } from '../../../services/Lookup.Service';
import { LoadingService } from '../../../services/loading.service';
import { DialogService } from '@progress/kendo-angular-dialog';
import { GenericData } from '../../../Models/Generic-Data';
import { UtilityService, NotificationType } from '../../../services/Utility.Service';
import { NotifierComponent } from '../../../notifier/notifier.component';
import { Title } from '@angular/platform-browser';
import { AppConstants } from '../../../app.constants';
import { SortDescriptor } from '@progress/kendo-data-query';
import { HttpErrorResponse } from '@angular/common/http';


@Component({
  selector: 'dashboard-management-component',
  templateUrl: './dashboard-management.component.html',
  styleUrls: ['./dashboard-management.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class DashboardManagement implements OnInit, OnDestroy {
  @Input() showHeader: boolean = true;

  private allDashboards: DashboardInfo[] = [];
  private allFolders: DashboardFolder[] = [];
  public gridData: any[] = [];
  private editedRowIndex: number;
  public dashForm: FormGroup;
  public privateFolders: DashboardFolder[] = [];
  public publicFolders: DashboardFolder[] = [];
  public privateDashboards: DashboardInfo[] = [];
  public publicDashboards: DashboardInfo[] = [];
  public isAdmin: boolean = false;
  public isBackOffice: boolean = false;
  public isProjectManager: boolean = false;
  public currentUserId: number = 0;
  private displayName: string = '';
  public canMakePublic: boolean = false;
  public stageMultiselectItems: GenericData[] = [];
  public selectedKeys: number[] = [];
  public selectableSettings: SelectableSettings = { "checkboxOnly": false, "mode": "multiple", "drag": true };
  public gridHeightFactor: number = this.showHeader ? 200 : 150;
  private subscriptions: Subscription = new Subscription();
  public sort: SortDescriptor[] = [
    {
    field: 'TypeName',
    dir: 'asc'
    },
    { field: 'Title', dir: 'asc'}
  ];

  @ViewChild("dashboardMgmtGrid") public dashboardMgmtGrid: GridComponent;

  @Output() messageFromDashManagement = new EventEmitter<any>();

  constructor(private utilService: UtilityService, private chartService: ChartService, private lookup: LookupService, private loadingService: LoadingService, private dialogService: DialogService, private notifier: NotifierComponent, private titleService: Title) { };

  public ngOnInit(): void {
    this.initializeData();
    document.querySelector('.modifiedNotification')?.classList.add('d-none');
    if (this.showHeader) {
      this.titleService.setTitle(AppConstants.APPLICATION_NAME + " Dashboard Management");
    }
  }

  public rowClass(r: RowClassArgs) {
    if (!r.dataItem.UserCanEditOrDelete) {
      return 'k-state-disabled cursorNotAllowed';
    }
  }

  public selectedKeysChange(e: number[]) {
    e.forEach(key => {
      let dash: any = this.gridData.find(d => d.Id == key)
      if (dash?.Folder?.Locked) {
        this.selectedKeys = this.selectedKeys.filter(sk => sk != dash.Id);
      }
    });
  }

  public multiDeleteConfirm() {
    let dashboardCount = this.selectedKeys.length == 1 ? 'selected dashboard' : this.selectedKeys.length + ' selected dashboards';
    let multiDeleteDlg = this.dialogService.open({
      title: 'Delete Dashboards',
      content: `Delete ${dashboardCount}?`,
      actions: [{
        text: 'Delete', primary: true, action: 'delete'
      }, { text: 'Cancel' }]
    });
    this.subscriptions.add(multiDeleteDlg.result.subscribe(x => {
      if (x['action'] == "delete") {
        this.loadingService.ShowLoading.next(true);
        let deleteCount: number = this.selectedKeys.length;
        let errorCount: number = 0;
        this.selectedKeys.forEach(k => {
          this.subscriptions.add(this.chartService.RemoveDashboard(k).subscribe(deleted => {
            deleteCount--;
            if (deleted) {
              let indx = this.gridData.findIndex(d => d.Id == k);
              this.gridData = this.gridData.filter(d => d.Id != k);
            }
            else {
              errorCount++;
            }
            if (deleteCount == 0) {
              this.postDeleteNotification(errorCount);
              this.selectedKeys = [];
            }
          },
            (e) => {
              this.postDeleteNotification(1);
            },
            () => { }));
        });
      }
    }));
  }

  private postDeleteNotification(errorCount: number) {
    this.loadingService.ShowLoading.next(false);
    if (errorCount < 1) {
      this.notifier.ShowNotifier(NotificationType.NOTIFIER_MEDIUMSUCCESS, "Dashboards Deleted", "Selected dashboards have been deleted");
    }
    else {
      this.notifier.ShowNotifier(NotificationType.NOTIFIER_ERROR, "Error", "There was an error deleting dashboards");
    }
  }

  public ngAfterViewInit(): void {
  }

  public initializeData() {
    DashboardTypeLabel.forEach((x, index) => {
      this.stageMultiselectItems.push({
        Id: index, Name: x
      });
    });

    this.subscriptions.add(this.lookup.GetUserView().subscribe(x => {
      this.currentUserId = x.UserId;
      this.displayName = x.DisplayName;
      if (x.roleInfo.IsAdmin) {
        this.isAdmin = true;
      }
      else if (x.roleInfo.IsBackOffice) {
        this.isBackOffice = true;
      }
      else if (x.roleInfo.IsProjectManager) {
        this.isProjectManager = true;
      }
    },
      e => { },
      () => {
        this.canMakePublic = this.isAdmin || this.isBackOffice || this.isProjectManager
        let fork;
        let getPrivateDashboards = this.chartService.GetDashboardsWithoutParts(false);
        let getPrivateFolders = this.chartService.GetDashboardFolders(false);
        let getPublicDashboards = this.chartService.GetDashboardsWithoutParts(true);
        let getPublicFolders = this.chartService.GetDashboardFolders(true);
        if (this.canMakePublic) {
          fork = forkJoin({
            'getPrivateFolders': getPrivateFolders, 'getPublicFolders': getPublicFolders, 'getPrivateDashboards': getPrivateDashboards, 'getPublicDashboards': getPublicDashboards
          });
        }
        else {
          fork = forkJoin({ 'getPrivateDashboards': getPrivateDashboards, 'getPrivateFolders': getPrivateFolders });
        }
        this.subscriptions.add(fork.subscribe(x => {
          this.privateDashboards = x.getPrivateDashboards;
          this.privateFolders = x.getPrivateFolders;
          x.getPublicDashboards?.forEach(dash => {
            if (this.canMakePublic) {
              this.publicDashboards.push(dash);
            }
          });
          x.getPublicFolders?.forEach(folder => {
            if (this.canMakePublic) {
              this.publicFolders.push(folder);
            }
          });
        },
          e => this.notifier.ShowNotifier(NotificationType.NOTIFIER_ERROR, "Error", "Error retrieving dashboards"),
          () => {//got all the data
            this.allDashboards = this.publicDashboards.length > 0 ? this.allDashboards.concat(this.privateDashboards, this.publicDashboards) : this.privateDashboards;
            this.allFolders = this.publicFolders.length > 0 ? this.allFolders.concat(this.privateFolders, this.publicFolders) : this.privateFolders;
            this.gridData = this.allDashboards;
            this.gridData.forEach((dashboard, index) => {
              this.gridData[index]['Folder'] = this.allFolders.find(folder => folder.FolderId === dashboard.FolderId) ?? new DashboardFolder();
              this.gridData[index]['CurrentUser'] = this.currentUserId;
              this.gridData[index]['UserCanEditOrDelete'] = this.userCanEditOrDelete(this.gridData[index]);
            });
            this.loadingService.ShowLoading.next(false);
          }));
      }));
  }

  private userCanEditOrDelete(gridItem: any): boolean {
    let canEditOrDelete: boolean = false;
    if (gridItem['Folder'].Locked != true) {
      if (gridItem.Shared) {       //public
        canEditOrDelete = this.isAdmin || this.isBackOffice || (this.isProjectManager && gridItem.Owner == this.currentUserId);
      }
      else {                       //private
        canEditOrDelete = gridItem.Owner == this.currentUserId;
      }
    }
    return canEditOrDelete;
  }

  public editDashboard({ sender, rowIndex, dataItem }) {
    this.closeEditor(sender);
    this.editedRowIndex = rowIndex;
    this.dashForm = new FormGroup({
      dashId: new FormControl(dataItem.Id),
      Title: new FormControl(dataItem.Title, Validators.required),
      Description: new FormControl(dataItem.Description),
      Shared: new FormControl(dataItem.Shared)
    });
    this.subscriptions.add(this.dashForm.get('Title').valueChanges.subscribe((x) => {
      let errors =
        this.lookupArray(this.dashForm.get('Shared').value, dataItem.DashboardType).findIndex(
          (d) => d.Title?.toLowerCase() == x.toLowerCase() && d.Id != dataItem.Id
        ) >= 0
          ? { message: 'Title already in use' }
          : null;

      this.dashForm.get('Title').setErrors(errors);
      if (x.length < 1)
        this.dashForm
          .get('Title')
          .setErrors({ message: 'Title cannot be blank' });
    }));
    this.subscriptions.add(this.dashForm.get('Shared').valueChanges.subscribe((x) => {
      if (!this.canMakePublic && this.dashForm.get('Shared').value === true) {
        this.dashForm.get('Shared').setValue(false);
      }
      let errors =
        this.lookupArray(this.dashForm.get('Shared').value, dataItem.DashboardType).findIndex(
          (d) => d.Title?.toLowerCase() == this.dashForm.get('Title').value && d.Id != dataItem.Id
        ) >= 0
          ? { message: 'Title already in use' }
          : null
      this.dashForm.get('Title').setErrors(errors);
    }));

    sender.editRow(rowIndex, this.dashForm);
  }

  public deleteDashboard({ dataItem }) {
    let dialog = this.dialogService.open({
      title: 'Delete Dashboard',
      content: 'Click Yes to delete, No to cancel',
      actions: [
        { text: 'Yes', primary: true },
        { text: 'No' }
      ]
    });
    this.subscriptions.add(dialog.result.subscribe((x: any) => {
      if (x.text === 'Yes') {
        this.gridData = this.gridData.filter(x => x != dataItem);
        this.subscriptions.add(this.chartService.RemoveDashboard(dataItem.Id).subscribe((x) => {
          if (x === false) {
            this.notifier.ShowNotifier(NotificationType.NOTIFIER_ERROR, "Error", "Error deleting dashboard");
            //console.error('error');
          }
        },
          e => { },
          () => {
            this.selectedKeys = [];
          }));
        this.messageFromDashManagement.emit({ data: dataItem, deleted: true });
      }
    }));
  }

  private lookupArray(shared: boolean, type) {
    return this.allDashboards.filter(x => x.Shared == shared && x.DashboardType == type);
  }

  public cancelHandler({ sender, rowIndex }) {
    this.closeEditor(sender, rowIndex);
  }

  private closeEditor(grid, rowIndex = this.editedRowIndex) {
    grid.closeRow(rowIndex);
    this.editedRowIndex = undefined;
    this.dashForm = undefined;
  }

  public saveDashboard({ sender, rowIndex, formGroup }) {
    let dashboardToSave: DashboardInfo = this.allDashboards.find(x => x.Id === formGroup.value.dashId);
    dashboardToSave.Title = formGroup.value.Title;
    dashboardToSave.Description = formGroup.value.Description;
    let initialSharedValue = dashboardToSave.Shared;
    let savedSharedValue = formGroup.value.Shared
    let sharedChanged = initialSharedValue != savedSharedValue;
    if (sharedChanged) {
      dashboardToSave.Shared = formGroup.value.Shared;
      dashboardToSave.Owner = this.currentUserId;
      this.gridData.find(d => d.Id == formGroup.value.dashId).spare = this.displayName;
      let originDashboards = this.allDashboards.filter(x => x.Shared == initialSharedValue && x.DashboardType == dashboardToSave.DashboardType && x.FolderId == dashboardToSave.FolderId);
      dashboardToSave.FolderId = null;
      dashboardToSave['Folder'] = null;
      let destinationDashboards = this.allDashboards.filter(x => x.Shared == savedSharedValue && x.DashboardType == dashboardToSave.DashboardType && !x.FolderId);
      dashboardToSave.SortOrder = destinationDashboards.length;
      originDashboards.forEach((x, i) => x.SortOrder = i);
      this.subscriptions.add(this.chartService.SaveDashboards(originDashboards).subscribe(x => { }, e => { this.notifier.ShowNotifier(NotificationType.NOTIFIER_ERROR, 'Error Saving Dashboard', e.error.errorMessage); }));
    }
    this.subscriptions.add(this.chartService.SaveDashboard(dashboardToSave, false).subscribe(x => { },
      (e: HttpErrorResponse) => {
        this.notifier.ShowNotifier(NotificationType.NOTIFIER_ERROR, 'Error Saving Dashboard', e.error.errorMessage);
  }));
    sender.closeRow(rowIndex);
    this.messageFromDashManagement.emit({ data: dashboardToSave, deleted: false });
  }

  public stageFilterChanged(stage: Array<GenericData>) {
    if (stage.length == 0) {
      this.gridData = this.allDashboards;
    }
    else {
      this.gridData = [];
      stage.forEach(st => {
        this.gridData = this.gridData.concat(this.allDashboards.filter(dash => dash.TypeName === st.Name));
      });
    }
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
